// Pure helper functions shared by the admin dashboard components.
import { CHECKLIST_SECTIONS } from "./constants";

export const formatSpecialty = (specialty) => {
    const specialtyMap = {
        'chiropractor': 'Chiropractor',
        'physical-therapy': 'Physical Therapy',
        'pain-management': 'Pain Management',
        'orthopedics': 'Orthopedics',
        'neurology': 'Neurology',
        'imaging': 'Imaging',
        'other': 'Other',
    };
    return specialtyMap[specialty] || specialty || '';
};

export const formatState = (location) => {
    const stateMap = {
        'arizona': 'Arizona',
        'texas': 'Texas',
        'colorado': 'Colorado',
        'nevada': 'Nevada',
        'new-mexico': 'New Mexico',
        'washington': 'Washington',
    };
    return stateMap[location] || location || '';
};

export const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
};

// Like formatDate but without seconds — e.g. "5/14/2026, 11:02 PM"
export const formatDateShort = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

export const formatActivityTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
};

// Records created before the enrollment-type feature default to online
export const getEnrollmentType = (submission) => (submission.enrollmentType === "paper" ? "paper" : "online");

export const getStatusColor = (submission) => {
    if (submission.enrollmentType === "paper") {
        return "paper";
    } else if (submission.termsAccepted && submission.signature) {
        return "completed";
    } else if (submission.termsAccepted) {
        return "pending";
    } else {
        return "incomplete";
    }
};

export const getStatusText = (submission) => {
    const status = getStatusColor(submission);
    switch (status) {
        case "paper":
            return "Paper Contract";
        case "completed":
            return "Complete";
        case "pending":
            return "Pending Signature";
        case "incomplete":
            return "Incomplete";
        default:
            return "Unknown";
    }
};

// All uploaded versions of a contractor document (oldest first). Records from
// before document versioning expose their single document as a one-item list.
export const getDocumentVersions = (submission, documentType) => {
    const versions = submission?.documentVersions?.[documentType];
    if (Array.isArray(versions)) return versions;
    const legacy = submission?.documents?.[documentType];
    return legacy ? [legacy] : [];
};

// Get document status icon for the table's Docs column
export const getDocumentStatusIcon = (submission) => {
    const hasW9 = !!submission.documents?.w9;
    const hasDD = !!submission.documents?.directDeposit;
    const hasSigned = !!submission.signedPdfUrl;

    const count = [hasW9, hasDD, hasSigned].filter(Boolean).length;
    if (count === 3) return '📁'; // All uploaded
    if (count > 0) return '📂'; // Partial
    return '—'; // None
};

// Get friendly label for an editable field
export const getFieldLabel = (fieldName) => {
    const labels = {
        name: "Name",
        email: "Email",
        phone: "Phone",
        specialty: "Specialty",
        practiceName: "Practice Name",
        dbaName: "DBA Name",
        location: "State",
        address: "Address",
        city: "City",
        zip: "ZIP",
        crossStreets: "Cross Streets",
        practicePhone: "Practice Phone",
        practiceFax: "Practice Fax",
        availableHours: "Available Hours",
        urgentAppointments: "Urgent Appointments",
        npi: "NPI Number",
        taxId: "Tax ID",
        licenseNumber: "License Number",
        referralEmail: "Referral Email",
        billingContact: "Billing Contact",
        billingEmail: "Billing Email",
        message: "Practice Description",
        enrollmentType: "Enrollment Type",
    };
    return labels[fieldName] || fieldName;
};

export const isChecklistItemComplete = (submission, item) => {
    if (item.auto && item.auto(submission)) return true;
    return !!submission?.onboardingChecklist?.items?.[item.key]?.completed;
};

export const getChecklistProgress = (submission) => {
    const allItems = CHECKLIST_SECTIONS.flatMap((section) => section.items);
    const done = allItems.filter((item) => isChecklistItemComplete(submission, item)).length;
    return { done, total: allItems.length };
};

export const getActivityIcon = (type, category) => {
    if (type === "note") {
        switch (category) {
            case "contact": return "📞";
            case "quarterly": return "📊";
            default: return "💬";
        }
    }
    switch (type) {
        case "edit": return "✏️";
        case "revert": return "↩️";
        case "checklist": return "✅";
        default: return "•";
    }
};

export const getCategoryLabel = (category) => {
    switch (category) {
        case "contact": return "Contact Log";
        case "quarterly": return "Quarterly Check-In";
        case "general": return "General";
        default: return "General";
    }
};

export const getCategoryColor = (category) => {
    switch (category) {
        case "contact": return "#17a2b8";
        case "quarterly": return "#6f42c1";
        default: return "#667eea";
    }
};
